import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileCopy as CopyIcon,
  Description as FileIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { excelTemplates } from '../templates/export/excelConfig';
import { pdfTemplates } from '../templates/export/pdfConfig';
import exportService from '../services/exportService';
import customTemplateService from '../services/customTemplateService';
import customTemplateServiceExcelJS from '../services/customTemplateServiceExcelJS';

function GestionPlantillas() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [customTemplates, setCustomTemplates] = useState({});

  useEffect(() => {
    // Cargar plantillas personalizadas al montar el componente
    const saved = localStorage.getItem('custom_excel_templates_v2');
    if (saved) {
      setCustomTemplates(JSON.parse(saved));
    }
  }, []);

  // Plantillas predefinidas
  const predefinedTemplates = {
    excel: [
      { id: 'standard', name: 'Estándar', description: 'Plantilla básica con todos los elementos' },
      { id: 'detallada', name: 'Detallada', description: 'Incluye análisis de precios unitarios' },
      { id: 'simplificada', name: 'Simplificada', description: 'Solo información esencial' },
      { id: 'corporativa', name: 'Corporativa', description: 'Formato corporativo profesional' }
    ],
    pdf: [
      { id: 'standard', name: 'Estándar', description: 'Formato PDF clásico' },
      { id: 'detallada', name: 'Detallada', description: 'PDF con información completa' },
      { id: 'ejecutiva', name: 'Ejecutiva', description: 'Resumen ejecutivo' },
      { id: 'minimalista', name: 'Minimalista', description: 'Diseño limpio y moderno' }
    ]
  };

  // Descargar plantilla de ejemplo
  const downloadTemplateExample = (type, templateId) => {
    // Crear datos de ejemplo
    const exampleData = {
      presupuesto: {
        codigo: 'PRE-2024-001',
        nombre_obra: 'Construcción de Edificio',
        cliente: 'Cliente Ejemplo S.A.',
        ubicacion: 'Ciudad de México',
        fecha: new Date().toISOString(),
        empresa: localStorage.getItem('empresa_nombre') || 'Mi Empresa S.A. de C.V.'
      },
      conceptos: [
        {
          codigo: 'PREL-001',
          descripcion: 'Preliminares',
          unidad: 'm2',
          cantidad: 100,
          precio_unitario: 150,
          importe: 15000
        },
        {
          codigo: 'CIM-001',
          descripcion: 'Cimentación',
          unidad: 'm3',
          cantidad: 50,
          precio_unitario: 2500,
          importe: 125000
        },
        {
          codigo: 'EST-001',
          descripcion: 'Estructura',
          unidad: 'ton',
          cantidad: 25,
          precio_unitario: 15000,
          importe: 375000
        }
      ],
      porcentajes: {
        indirectos: 15,
        financiamiento: 3,
        utilidad: 10,
        iva: 16
      }
    };

    if (type === 'excel') {
      // Usar el servicio de exportación para generar el Excel
      exportService.exportToExcel(
        exampleData.presupuesto,
        exampleData.conceptos,
        exampleData.porcentajes,
        templateId
      );
      toast.success(`Plantilla ${templateId} descargada como ejemplo`);
    } else if (type === 'pdf') {
      exportService.exportToPDF(
        exampleData.presupuesto,
        exampleData.conceptos,
        exampleData.porcentajes,
        templateId
      );
      toast.success(`Plantilla PDF ${templateId} descargada como ejemplo`);
    }
  };

  // Manejar subida de archivo
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Usar ExcelJS para cargar el archivo
      const templateData = await customTemplateServiceExcelJS.loadTemplateFromFile(file);

      setUploadedFile({
        name: file.name,
        buffer: templateData.buffer,
        worksheetCount: templateData.worksheetCount,
        sheetNames: templateData.sheetNames
      });

      toast.success('Archivo cargado correctamente con todos los estilos, imágenes y formato preservados');
      setUploadDialogOpen(true);
    } catch (error) {
      toast.error('Error al leer el archivo Excel: ' + error.message);
      console.error(error);
    }
  };

  // Guardar plantilla personalizada
  const saveCustomTemplate = async () => {
    if (!templateName) {
      toast.error('Por favor ingresa un nombre para la plantilla');
      return;
    }

    // Usar el servicio ExcelJS para guardar con el buffer completo
    await customTemplateServiceExcelJS.saveTemplate(
      templateName,
      { name: uploadedFile.name },
      uploadedFile.buffer
    );

    // Actualizar el estado local
    const customTemplates = JSON.parse(localStorage.getItem('custom_excel_templates_v2') || '{}');
    setCustomTemplates(customTemplates);

    toast.success('Plantilla personalizada guardada con todos los estilos');
    setUploadDialogOpen(false);
    setTemplateName('');
    setUploadedFile(null);
  };

  // Descargar plantilla en blanco para editar
  const downloadBlankTemplate = () => {
    // Crear un libro de Excel en blanco con estructura básica
    const wb = XLSX.utils.book_new();

    // Crear hoja con estructura básica
    const wsData = [
      ['[EMPRESA]'],
      ['PRESUPUESTO DE OBRA'],
      [],
      ['Cliente:', '[CLIENTE]'],
      ['Obra:', '[OBRA]'],
      ['Ubicación:', '[UBICACION]'],
      ['Fecha:', '[FECHA]'],
      [],
      ['CONCEPTOS'],
      ['Código', 'Descripción', 'Unidad', 'Cantidad', 'P.U.', 'Importe'],
      ['[DATOS]'],
      [],
      ['', '', '', '', 'Subtotal:', '[SUBTOTAL]'],
      ['', '', '', '', 'Indirectos ([IND]%):', '[INDIRECTOS]'],
      ['', '', '', '', 'Financiamiento ([FIN]%):', '[FINANCIAMIENTO]'],
      ['', '', '', '', 'Utilidad ([UTI]%):', '[UTILIDAD]'],
      ['', '', '', '', 'IVA (16%):', '[IVA]'],
      ['', '', '', '', 'TOTAL:', '[TOTAL]']
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Ajustar anchos de columna
    ws['!cols'] = [
      { wch: 15 },  // Código
      { wch: 50 },  // Descripción
      { wch: 10 },  // Unidad
      { wch: 12 },  // Cantidad
      { wch: 15 },  // P.U.
      { wch: 15 }   // Importe
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Presupuesto');

    // Agregar hoja de instrucciones
    const instructionsData = [
      ['INSTRUCCIONES PARA PERSONALIZAR LA PLANTILLA'],
      [],
      ['Variables disponibles:'],
      ['[EMPRESA]', 'Nombre de la empresa'],
      ['[CLIENTE]', 'Nombre del cliente'],
      ['[OBRA]', 'Nombre de la obra'],
      ['[UBICACION]', 'Ubicación de la obra'],
      ['[FECHA]', 'Fecha del presupuesto'],
      ['[DATOS]', 'Aquí se insertarán los conceptos'],
      ['[SUBTOTAL]', 'Subtotal calculado'],
      ['[IND]', 'Porcentaje de indirectos'],
      ['[INDIRECTOS]', 'Monto de indirectos'],
      ['[FIN]', 'Porcentaje de financiamiento'],
      ['[FINANCIAMIENTO]', 'Monto de financiamiento'],
      ['[UTI]', 'Porcentaje de utilidad'],
      ['[UTILIDAD]', 'Monto de utilidad'],
      ['[IVA]', 'Monto de IVA'],
      ['[TOTAL]', 'Total general'],
      [],
      ['NOTAS:'],
      ['- Puedes modificar el diseño, colores y formato'],
      ['- Mantén las variables entre corchetes []'],
      ['- La fila con [DATOS] será reemplazada por los conceptos'],
      ['- Puedes agregar tu logo y estilos personalizados']
    ];

    const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instrucciones');

    // Descargar
    XLSX.writeFile(wb, 'plantilla_presupuesto_editable.xlsx');
    toast.success('Plantilla en blanco descargada');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestión de Plantillas
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Descarga plantillas de ejemplo para ver el formato, o sube tu propia plantilla Excel personalizada.
        Las plantillas se pueden editar directamente en el código en la carpeta <code>/templates/export/</code>
      </Alert>

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={downloadBlankTemplate}
        >
          Descargar Plantilla en Blanco
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Subir Plantilla Excel
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
      </Box>

      {/* Plantillas Personalizadas */}
      {Object.keys(customTemplates).length > 0 && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            <FileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Plantillas Personalizadas
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {Object.entries(customTemplates).map(([key, template]) => (
              <Grid item xs={12} md={6} lg={3} key={key}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{template.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subida el {new Date(template.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        const updated = { ...customTemplates };
                        delete updated[key];
                        localStorage.setItem('custom_excel_templates_v2', JSON.stringify(updated));
                        setCustomTemplates(updated);
                        toast.success('Plantilla eliminada');
                      }}
                    >
                      Eliminar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Plantillas Excel */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        <ExcelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Plantillas Excel Predefinidas
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {predefinedTemplates.excel.map(template => (
          <Grid item xs={12} md={6} lg={3} key={template.id}>
            <Card>
              <CardContent>
                <Typography variant="h6">{template.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {template.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadTemplateExample('excel', template.id)}
                >
                  Descargar
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    toast('Edita el archivo en /templates/export/excelConfig.js', { icon: '📝' });
                  }}
                >
                  Editar
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Plantillas PDF */}
      <Typography variant="h5" gutterBottom>
        <PdfIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Plantillas PDF
      </Typography>
      <Grid container spacing={3}>
        {predefinedTemplates.pdf.map(template => (
          <Grid item xs={12} md={6} lg={3} key={template.id}>
            <Card>
              <CardContent>
                <Typography variant="h6">{template.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {template.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadTemplateExample('pdf', template.id)}
                >
                  Descargar
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    toast('Edita el archivo en /templates/export/pdfConfig.js', { icon: '📝' });
                  }}
                >
                  Editar
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog para guardar plantilla subida */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Guardar Plantilla Personalizada</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Archivo cargado: {uploadedFile?.name}
          </Alert>
          <TextField
            fullWidth
            label="Nombre de la Plantilla"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            margin="normal"
            helperText="Este nombre se usará para identificar tu plantilla"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveCustomTemplate}>
            Guardar Plantilla
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 4 }} />

      <Alert severity="warning">
        <Typography variant="subtitle2" gutterBottom>
          Para modificar las plantillas directamente:
        </Typography>
        <Typography variant="body2">
          1. Abre el archivo <code>/frontend/src/templates/export/excelConfig.js</code> para Excel
        </Typography>
        <Typography variant="body2">
          2. Abre el archivo <code>/frontend/src/templates/export/pdfConfig.js</code> para PDF
        </Typography>
        <Typography variant="body2">
          3. Modifica los estilos, columnas y estructura según necesites
        </Typography>
        <Typography variant="body2">
          4. Los cambios se aplicarán automáticamente al recargar la página
        </Typography>
      </Alert>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/configuracion-plantillas')}
        >
          Volver a Configuración
        </Button>
      </Box>
    </Box>
  );
}

export default GestionPlantillas;